import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseHttpService } from './base-http.service';
import {
  PolicyDetail,
  PolicySummary,
  PolicyCancelRequest,
  PolicyChangeRequest,
  PolicyCancelHistory,
  PolicyChangeHistory,
  PolicyChangeDetailFull,
  PolicyBenefit,
  InsuredDetails,
  IHPolicyForPayPremiumDto
} from '../models';

/**
 * Policy Service for IH API
 * Handles policy details, cancellation, and change requests
 */
@Injectable({
  providedIn: 'root'
})
export class PolicyService extends BaseHttpService {
  /**
   * Get policy benefits
   * GET /api/ih/policy/benefits/{policyNo}
   */
  getPolicyBenefits(policyNo: string): Observable<PolicyBenefit[]> {
    return this.get<PolicyBenefit[]>(`${this.POLICY_ENDPOINT}/benefits/${policyNo}`);
  }

  /**
   * Get insured details
   * GET /api/ih/policy/insured/{insuredCode}
   */
  getInsuredDetails(insuredCode: string): Observable<InsuredDetails> {
    return this.get<InsuredDetails>(`${this.POLICY_ENDPOINT}/insured/${insuredCode}`);
  }

  private readonly POLICY_ENDPOINT = 'policy';

  /**
   * Get all policies by phone number
   * GET /api/ih/policy/by-phone/{phone}
   * 
   * @deprecated This endpoint returns inconsistent field names (descLa, insuredNameLa, coverageAmt).
   * Use HomeService.getUserPolicies() instead, which returns standardized fields
   * (productName, insuredName, coverageAmount, productIcon) for UI display purposes.
   * 
   * This method may still be used for backend operations that specifically require
   * the legacy field structure.
   */
  getPoliciesByPhone(phone: string): Observable<PolicySummary[]> {
    console.warn('⚠️ PolicyService.getPoliciesByPhone() is deprecated. Use HomeService.getUserPolicies() for UI display.');
    return this.get<PolicySummary[]>(`${this.POLICY_ENDPOINT}/by-phone/${phone}`);
  }

  /**
   * Get policy details
   * GET /api/ih/policy/details/{policyNo}
   */
  getPolicyDetails(policyNo: string): Observable<PolicyDetail> {
    return this.get<PolicyDetail>(`${this.POLICY_ENDPOINT}/details/${policyNo}`);
  }

  /**
   * Submit policy cancellation request
   * POST /api/ih/policy/cancelation
   */
  submitCancellation(request: PolicyCancelRequest): Observable<any> {
    return this.post<any>(`${this.POLICY_ENDPOINT}/cancelation`, request);
  }

  /**
   * Get cancellation history
   * GET /api/ih/policy/cancel-history/{phone}
   */
  getCancellationHistory(phone: string): Observable<PolicyCancelHistory[]> {
    return this.get<PolicyCancelHistory[]>(`${this.POLICY_ENDPOINT}/cancel-history/${phone}`);
  }

  /**
   * Submit policy change request
   * POST /api/ih/policy/change-request
   */
  submitChangeRequest(request: PolicyChangeRequest): Observable<any> {
    return this.post<any>(`${this.POLICY_ENDPOINT}/change-request`, request);
  }

  /**
   * Get change request history
   * GET /api/ih/policy/change-request-history/{phone}
   */
  getChangeRequestHistory(phone: string): Observable<PolicyChangeHistory[]> {
    return this.get<PolicyChangeHistory[]>(`${this.POLICY_ENDPOINT}/change-request-history/${phone}`);
  }

  /**
   * Get policy change request details with full information
   * GET /api/ih/policy/change-request/{id}
   */
  getChangeRequestDetail(id: string): Observable<PolicyChangeDetailFull> {
    return this.get<PolicyChangeDetailFull>(`${this.POLICY_ENDPOINT}/change-request/${id}`);
  }

  /**
   * Get policy cancellation list
   * GET /api/ih/policy/cancellation-list/{policyNo}
   */
  getPolicyCancellationList(policyNo: string, phone: string): Observable<any[]> {
    return this.get<any[]>(`${this.POLICY_ENDPOINT}/cancellation-list/${policyNo}?phone=${phone}`);
  }

  /**
   * Get policy cancellation detail
   * GET /api/ih/policy/cancellation-detail/{cancellationId}
   */
  getCancellationDetail(cancellationId: string): Observable<any> {
    return this.get<any>(`${this.POLICY_ENDPOINT}/cancellation-detail/${cancellationId}`);
  }

  /**
   * Get policy for pay premium
   * POST /api/ih/policy/pay-premium
   */
  getPolicyForPayPremium(params: any): Observable<IHPolicyForPayPremiumDto> {
    return this.post<IHPolicyForPayPremiumDto>(`${this.POLICY_ENDPOINT}/pay-premium`, params);
  }
}
